import { nl } from "../i18n/nl";

export const API_BASE = "/api/v1";

/** Error returned by the API (`{ error: { code, message } }`) or by the network layer. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** Optional `error.details` (e.g. `findings` for `contains_personal_data`). */
  readonly details: Record<string, unknown> | undefined;

  constructor(status: number, code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** Dutch text for an error code; falls back to the generic message. */
export function errorText(error: unknown): string {
  if (error instanceof ApiError && error.code in nl.errors) {
    return nl.errors[error.code as keyof typeof nl.errors];
  }
  return nl.errors.unknown_error;
}

function isErrorBody(
  value: unknown,
): value is { error: { code: string; message: string; details?: Record<string, unknown> } } {
  if (typeof value !== "object" || value === null || !("error" in value)) return false;
  const error = value.error;
  return (
    typeof error === "object" &&
    error !== null &&
    typeof (error as { code?: unknown }).code === "string" &&
    typeof (error as { message?: unknown }).message === "string"
  );
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  /** Serialized as JSON. */
  json?: unknown;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { json, headers, ...init } = options;
  const requestHeaders = new Headers(headers);
  requestHeaders.set("Accept", "application/json");
  if (json !== undefined) requestHeaders.set("Content-Type", "application/json");

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: requestHeaders,
      body: json === undefined ? undefined : JSON.stringify(json),
      credentials: "include",
    });
  } catch {
    throw new ApiError(0, "network_error", nl.errors.network_error);
  }

  const body = await readJson(response);
  if (!response.ok) {
    if (isErrorBody(body)) {
      throw new ApiError(response.status, body.error.code, body.error.message, body.error.details);
    }
    throw new ApiError(response.status, "unknown_error", nl.errors.unknown_error);
  }
  return body as T;
}
